/**
 * Attendance Management - Full Featured Page
 * Tabs: Dashboard, Mark Attendance, Reports
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

// ==================== STAT CARD ====================
function StatCard({ label, value, sub, color = 'orange', icon }) {
  const colors = {
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div data-testid={`stat-${color}`} className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium opacity-80">{label}</span>
        {icon && <span className="text-lg">{icon}</span>}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

// ==================== DASHBOARD TAB ====================
function DashboardTab({ token, language }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/attendance/dashboard-stats', token);
      if (res.success) setStats(res);
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="p-8 text-center text-gray-500">{language === 'hi' ? 'लोड हो रहा है...' : 'Loading...'}</div>;
  if (!stats) return <div className="p-8 text-center text-gray-500">{language === 'hi' ? 'कोई डेटा नहीं' : 'No data available'}</div>;

  const { today, total_students, trend, class_attendance } = stats;

  return (
    <div className="space-y-6" data-testid="attendance-dashboard-tab">
      {/* Today's Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label={language === 'hi' ? 'कुल छात्र' : 'Total Students'} value={total_students} color="blue" icon="👨‍🎓" />
        <StatCard label={language === 'hi' ? 'उपस्थित' : 'Present'} value={today.present} color="green" icon="✅" />
        <StatCard label={language === 'hi' ? 'अनुपस्थित' : 'Absent'} value={today.absent} color="red" icon="❌" />
        <StatCard label={language === 'hi' ? 'देरी से' : 'Late'} value={today.late} color="yellow" icon="⏰" />
        <StatCard label={language === 'hi' ? 'नहीं लगी' : 'Not Marked'} value={today.not_marked} color="slate" icon="❓" />
        <StatCard
          label={language === 'hi' ? 'उपस्थिति दर' : 'Attendance Rate'}
          value={`${today.attendance_rate}%`}
          color="orange"
          icon="📊"
        />
      </div>

      {/* 7-Day Trend */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-gray-800 mb-4">
          {language === 'hi' ? '7 दिन का ट्रेंड' : '7-Day Trend'}
        </h3>
        <div className="flex items-end gap-2 h-40">
          {trend.map((d, i) => {
            const maxVal = Math.max(...trend.map(t => t.total), 1);
            const pctH = d.total > 0 ? (d.present / d.total * 100) : 0;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-gray-100 rounded-t relative" style={{ height: `${Math.max((d.total / maxVal) * 120, 4)}px` }}>
                  <div
                    className="absolute bottom-0 w-full bg-green-400 rounded-t transition-all"
                    style={{ height: `${pctH}%` }}
                  />
                </div>
                <span className="text-[10px] text-gray-500">{d.date.slice(5)}</span>
                {d.total > 0 && <span className="text-[10px] font-medium text-green-600">{Math.round(pctH)}%</span>}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded" /> {language === 'hi' ? 'उपस्थित' : 'Present'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-200 rounded" /> {language === 'hi' ? 'कुल' : 'Total'}</span>
        </div>
      </div>

      {/* Class-wise attendance */}
      {class_attendance.length > 0 && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">
            {language === 'hi' ? 'आज की कक्षा-वार उपस्थिति' : "Today's Class-wise Attendance"}
          </h3>
          <div className="space-y-3">
            {class_attendance.map((ca) => {
              const pct = ca.total > 0 ? Math.round(ca.present / ca.total * 100) : 0;
              return (
                <div key={ca.class_id} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-24 text-gray-700">{ca.class_name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                    <div className="h-full bg-green-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-gray-700">{pct}% ({ca.present}/{ca.total})</span>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="text-red-500">{ca.absent}A</span>
                    <span className="text-yellow-500">{ca.late}L</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MARK ATTENDANCE TAB ====================
function MarkAttendanceTab({ token, language }) {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [existingCount, setExistingCount] = useState(0);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/classes/', token);
      setClasses(res.classes || []);
      setLoading(false);
    })();
  }, [token]);

  const loadStudents = useCallback(async () => {
    if (!selectedClass || !selectedSection) return;
    const res = await api.get(`/api/students/?class_id=${selectedClass}&section_id=${selectedSection}`, token);
    setStudents(res.students || []);

    const attRes = await api.get(`/api/attendance/class/${selectedClass}/${selectedSection}?date=${selectedDate}`, token);
    const attMap = {};
    (attRes.attendance || []).forEach(a => { attMap[a.student_id] = a.status; });
    setAttendance(attMap);
    setExistingCount(Object.keys(attMap).length);
  }, [selectedClass, selectedSection, selectedDate, token]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const handleChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAll = (status) => {
    const newAtt = {};
    students.forEach(s => { newAtt[s.id] = status; });
    setAttendance(newAtt);
  };

  const handleSave = async () => {
    if (!selectedClass || !selectedSection) return;
    setSaving(true);
    const records = students.map(s => ({ student_id: s.id, status: attendance[s.id] || 'present' }));
    const res = await api.post('/api/attendance/mark', {
      class_id: selectedClass,
      section_id: selectedSection,
      date: new Date(selectedDate).toISOString(),
      records
    }, token);
    setSaving(false);
    if (res.success) {
      setToast({ message: language === 'hi' ? 'उपस्थिति सेव हो गई!' : 'Attendance saved!', type: 'success' });
      setExistingCount(students.length);
      setTimeout(() => setToast(null), 3000);
    } else {
      setToast({ message: res.detail || 'Error saving', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const sections = classes.find(c => c.id === selectedClass)?.sections || [];
  const presentCount = Object.values(attendance).filter(s => s === 'present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'absent').length;
  const lateCount = Object.values(attendance).filter(s => s === 'late').length;

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="mark-attendance-tab">
      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select data-testid="class-select" value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); setSelectedSection(''); setStudents([]); setAttendance({}); }}
            className="px-3 py-2 border rounded-lg text-sm bg-white">
            <option value="">{language === 'hi' ? 'कक्षा चुनें' : 'Select Class'}</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="section-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white" disabled={!selectedClass}>
            <option value="">{language === 'hi' ? 'सेक्शन चुनें' : 'Select Section'}</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.name}</option>)}
          </select>
          <input data-testid="date-input" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm" />
          <div className="flex gap-2">
            <button data-testid="mark-all-present" onClick={() => handleMarkAll('present')}
              className="flex-1 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm hover:bg-green-100 transition">
              {language === 'hi' ? 'सभी उपस्थित' : 'All Present'}
            </button>
            <button data-testid="mark-all-absent" onClick={() => handleMarkAll('absent')}
              className="flex-1 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm hover:bg-red-100 transition">
              {language === 'hi' ? 'सभी अनुपस्थित' : 'All Absent'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      {students.length > 0 && (
        <div className="bg-white rounded-xl border p-3 flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-gray-600">{language === 'hi' ? 'कुल' : 'Total'}: {students.length}</span>
          <span className="text-green-600 font-medium">✅ {presentCount}</span>
          <span className="text-red-600 font-medium">❌ {absentCount}</span>
          <span className="text-yellow-600 font-medium">⏰ {lateCount}</span>
          {existingCount > 0 && (
            <span className="ml-auto px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-medium">
              {language === 'hi' ? 'पहले से सेव' : 'Already saved'}
            </span>
          )}
        </div>
      )}

      {/* Student Table */}
      {students.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="attendance-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{language === 'hi' ? 'नाम' : 'Name'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{language === 'hi' ? 'रोल नं.' : 'Adm No.'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-green-600 uppercase">{language === 'hi' ? 'उपस्थित' : 'Present'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-red-600 uppercase">{language === 'hi' ? 'अनुपस्थित' : 'Absent'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-yellow-600 uppercase">{language === 'hi' ? 'देरी' : 'Late'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-blue-600 uppercase">{language === 'hi' ? 'आधा दिन' : 'Half Day'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {students.map((student, idx) => {
                  const status = attendance[student.id] || 'present';
                  const rowBg = status === 'absent' ? 'bg-red-50/50' : status === 'late' ? 'bg-yellow-50/50' : status === 'half_day' ? 'bg-blue-50/50' : '';
                  return (
                    <tr key={student.id} className={`hover:bg-gray-50 transition ${rowBg}`}>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{student.name}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{student.admission_number || '-'}</td>
                      {['present', 'absent', 'late', 'half_day'].map(s => (
                        <td key={s} className="px-4 py-2.5 text-center">
                          <input
                            type="radio"
                            name={`att-${student.id}`}
                            checked={status === s}
                            onChange={() => handleChange(student.id, s)}
                            className={`w-4 h-4 cursor-pointer ${s === 'present' ? 'accent-green-500' : s === 'absent' ? 'accent-red-500' : s === 'late' ? 'accent-yellow-500' : 'accent-blue-500'}`}
                            data-testid={`att-${student.id}-${s}`}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t bg-gray-50">
            <button data-testid="save-attendance-btn" onClick={handleSave} disabled={saving}
              className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
              {saving ? (language === 'hi' ? 'सेव हो रहा है...' : 'Saving...') : (language === 'hi' ? 'उपस्थिति सेव करें' : 'Save Attendance')}
            </button>
          </div>
        </div>
      )}

      {selectedClass && selectedSection && students.length === 0 && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-4xl mb-2">👨‍🎓</div>
          {language === 'hi' ? 'इस सेक्शन में कोई छात्र नहीं' : 'No students in this section'}
        </div>
      )}

      {!selectedClass && (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">📋</div>
          <p className="text-lg">{language === 'hi' ? 'कक्षा और सेक्शन चुनें' : 'Select a class and section to begin'}</p>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ==================== REPORTS TAB ====================
function ReportsTab({ token, language }) {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('class');

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/classes/', token);
      setClasses(res.classes || []);
    })();
  }, [token]);

  const loadClassReport = async () => {
    if (!selectedClass || !selectedSection) return;
    setLoading(true);
    const res = await api.get(
      `/api/attendance/class-range/${selectedClass}/${selectedSection}?start_date=${startDate}&end_date=${endDate}`, token
    );
    setReportData(res);
    setLoading(false);
  };

  const sections = classes.find(c => c.id === selectedClass)?.sections || [];

  return (
    <div className="space-y-4" data-testid="reports-tab">
      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select data-testid="report-class-select" value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); setSelectedSection(''); }}
            className="px-3 py-2 border rounded-lg text-sm bg-white">
            <option value="">{language === 'hi' ? 'कक्षा चुनें' : 'Select Class'}</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="report-section-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white" disabled={!selectedClass}>
            <option value="">{language === 'hi' ? 'सेक्शन चुनें' : 'Section'}</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.name}</option>)}
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
          <button data-testid="load-report-btn" onClick={loadClassReport} disabled={!selectedClass || !selectedSection || loading}
            className="py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {loading ? '...' : (language === 'hi' ? 'रिपोर्ट देखें' : 'View Report')}
          </button>
        </div>
      </div>

      {/* Report Data */}
      {reportData && reportData.attendance_by_date && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">
              {language === 'hi' ? 'दैनिक उपस्थिति रिपोर्ट' : 'Daily Attendance Report'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {language === 'hi' ? `कुल रिकॉर्ड: ${reportData.total_records}` : `Total Records: ${reportData.total_records}`}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'दिनांक' : 'Date'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-green-600">✅ {language === 'hi' ? 'उपस्थित' : 'Present'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-red-600">❌ {language === 'hi' ? 'अनुपस्थित' : 'Absent'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-yellow-600">⏰ {language === 'hi' ? 'देरी' : 'Late'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-blue-600">{language === 'hi' ? 'दर' : 'Rate'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(reportData.attendance_by_date)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([date, stats]) => {
                    const total = stats.present + stats.absent + stats.late + (stats.half_day || 0);
                    const rate = total > 0 ? Math.round(stats.present / total * 100) : 0;
                    return (
                      <tr key={date} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-sm font-medium">{date}</td>
                        <td className="px-4 py-2.5 text-center text-sm text-green-600 font-medium">{stats.present}</td>
                        <td className="px-4 py-2.5 text-center text-sm text-red-600">{stats.absent}</td>
                        <td className="px-4 py-2.5 text-center text-sm text-yellow-600">{stats.late}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${rate >= 80 ? 'bg-green-100 text-green-700' : rate >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                            {rate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!reportData && (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">📊</div>
          <p>{language === 'hi' ? 'कक्षा चुनें और रिपोर्ट देखें' : 'Select class and view report'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function AttendanceFullPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: language === 'hi' ? 'डैशबोर्ड' : 'Dashboard', icon: '📊' },
    { id: 'mark', label: language === 'hi' ? 'उपस्थिति लगाएं' : 'Mark Attendance', icon: '✅' },
    { id: 'reports', label: language === 'hi' ? 'रिपोर्ट' : 'Reports', icon: '📋' },
  ];

  return (
    <div className="space-y-4" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {language === 'hi' ? 'उपस्थिति प्रबंधन' : 'Attendance Management'}
          </h1>
          <p className="text-sm text-gray-500">
            {language === 'hi' ? 'मैन्युअल और बायोमेट्रिक उपस्थिति' : 'Manual & Biometric Attendance'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="attendance-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === tab.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <span className="mr-1.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardTab token={token} language={language} />}
      {activeTab === 'mark' && <MarkAttendanceTab token={token} language={language} />}
      {activeTab === 'reports' && <ReportsTab token={token} language={language} />}
    </div>
  );
}
